import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  ArrowLeft, 
  Building2, 
  User, 
  Mail, 
  Phone,
  Globe,
  MapPin,
  Shield,
  CheckCircle2,
  Loader2,
  ArrowRight,
  Briefcase,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Plan info
const PLANS = {
  SMALL_BUSINESS: { name: 'Small Business', price: '$250/mo', color: 'slate' },
  GROWTH: { name: 'Growth', price: '$950/mo', color: 'teal' },
  ENTERPRISE: { name: 'Enterprise', price: 'Custom', color: 'purple' },
};

// Business size options
const BUSINESS_SIZES = [
  { value: 'small', label: 'Small Business' },
  { value: 'wosb', label: 'Women-Owned Small Business (WOSB)' },
  { value: '8a', label: 'SBA 8(a)' },
  { value: 'hubzone', label: 'HUBZone' },
  { value: 'sdvosb', label: 'Service-Disabled Veteran-Owned (SDVOSB)' },
  { value: 'large', label: 'Large Business' },
];

// Industry sectors
const INDUSTRY_SECTORS = [
  { value: 'defense', label: 'Defense & Aerospace' },
  { value: 'it', label: 'Information Technology' },
  { value: 'healthcare', label: 'Healthcare & Life Sciences' },
  { value: 'energy', label: 'Energy & Environment' },
  { value: 'logistics', label: 'Logistics & Transportation' },
  { value: 'professional', label: 'Professional Services' },
  { value: 'construction', label: 'Construction & Engineering' },
  { value: 'other', label: 'Other' },
];

export default function ClientRegistrationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [error, setError] = useState(null);
  
  // Get plan from URL params
  const selectedPlan = searchParams.get('plan') || 'GROWTH';
  const planInfo = PLANS[selectedPlan] || PLANS.GROWTH;
  
  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Organization Info
    companyName: '',
    dba: '',
    uei: '',
    cage: '',
    businessSize: '',
    industrySector: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    
    // Step 2: Primary Admin
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPhone: '',
    adminTitle: '',
    
    // Step 3: Account Setup (password removed — login is OTP-only)
    agreeTerms: false,
    agreePrivacy: false,
    linkConsultant: '',
  });

  // Generate a random password for the backend (non-SA users log in via OTP)
  const _generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    return Array.from({ length: 24 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      // Call the real registration API
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: formData.companyName,
          uei: formData.uei || null,
          cage_code: formData.cage || null,
          business_size: formData.businessSize,
          industry_sector: formData.industrySector,
          website: formData.website || null,
          address_line1: formData.address || null,
          city: formData.city || null,
          state: formData.state || null,
          zip_code: formData.zip || null,
          first_name: formData.adminFirstName,
          last_name: formData.adminLastName,
          email: formData.adminEmail,
          password: _generateRandomPassword(), // Non-SA users are OTP-only; backend still expects a field
          phone: formData.adminPhone || null,
          title: formData.adminTitle || null,
          subscription_plan: selectedPlan,
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
      
      // Auth token comes in as an HttpOnly cookie — don't persist it in JS storage.
      // (The backend Set-Cookie on registration handles session continuity.)
      localStorage.removeItem('auth_token');
      
      setRegistrationComplete(true);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const canProceedStep1 = formData.companyName && formData.businessSize && formData.industrySector;
  const canProceedStep2 = formData.adminFirstName && formData.adminLastName && formData.adminEmail;
  const canSubmit = formData.agreeTerms && formData.agreePrivacy;

  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg border-slate-200 shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Registration Complete!
            </h2>
            <p className="text-slate-600 mb-4">
              Your account has been created with the <strong>{planInfo.name}</strong> plan.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-slate-500 mb-2">Your Workspace:</p>
              <p className="text-lg font-semibold text-slate-900">{formData.companyName}</p>
              <p className="text-sm text-slate-500 mt-2">Plan: <Badge className="bg-teal-100 text-teal-700 ml-1">{planInfo.name}</Badge></p>
            </div>
            <Button 
              className="w-full bg-teal-700 hover:bg-teal-800 text-white"
              onClick={() => navigate('/login')}
              data-testid="go-to-login-btn"
            >
              Go to Login
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-white hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 bg-teal-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">F</span>
            </div>
            <span className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>FedBD</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge className={`bg-${planInfo.color}-500/20 text-${planInfo.color}-300 border-${planInfo.color}-500/30`}>
              <Sparkles className="w-3 h-3 mr-1" />
              {planInfo.name} Plan
            </Badge>
            <Link to="/login" className="text-slate-300 hover:text-white text-sm flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-10">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                step >= s ? 'bg-teal-600 text-white' : 'bg-slate-700 text-slate-400'
              }`}>
                {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-20 h-1 mx-2 rounded ${step > s ? 'bg-teal-600' : 'bg-slate-700'}`} />
              )}
            </div>
          ))}
        </div>

        <Card className="border-slate-200 shadow-2xl" data-testid="client-registration-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50">
            <CardTitle className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {step === 1 && 'Organization Information'}
              {step === 2 && 'Primary Administrator'}
              {step === 3 && 'Account Setup'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Tell us about your organization to set up your workspace'}
              {step === 2 && 'This person will be the Contractor Admin with full access'}
              {step === 3 && 'Review terms and complete registration'}
            </CardDescription>
          </CardHeader>

          <CardContent className="p-8">
            <form onSubmit={handleSubmit}>
              {/* STEP 1: Organization Info */}
              {step === 1 && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="companyName">Legal Company Name *</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="companyName"
                          placeholder="Acme Defense Systems, LLC"
                          value={formData.companyName}
                          onChange={(e) => updateFormData('companyName', e.target.value)}
                          className="pl-10 border-slate-200"
                          data-testid="company-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dba">DBA (Doing Business As)</Label>
                      <Input
                        id="dba"
                        placeholder="Acme Defense"
                        value={formData.dba}
                        onChange={(e) => updateFormData('dba', e.target.value)}
                        className="border-slate-200"
                        data-testid="dba-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="uei">UEI (Unique Entity ID)</Label>
                      <Input
                        id="uei"
                        placeholder="e.g., H93L94K2M1P5"
                        value={formData.uei}
                        onChange={(e) => updateFormData('uei', e.target.value.toUpperCase())}
                        className="border-slate-200 font-mono"
                        maxLength={12}
                        data-testid="uei-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cage">CAGE Code</Label>
                      <Input
                        id="cage"
                        placeholder="e.g., 7Z9X2"
                        value={formData.cage}
                        onChange={(e) => updateFormData('cage', e.target.value.toUpperCase())}
                        className="border-slate-200 font-mono"
                        maxLength={5}
                        data-testid="cage-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Business Size/Set-Aside *</Label>
                      <Select value={formData.businessSize} onValueChange={(v) => updateFormData('businessSize', v)}>
                        <SelectTrigger className="border-slate-200" data-testid="business-size-select">
                          <SelectValue placeholder="Select business size" />
                        </SelectTrigger>
                        <SelectContent>
                          {BUSINESS_SIZES.map((size) => (
                            <SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Primary Industry Sector *</Label>
                      <Select value={formData.industrySector} onValueChange={(v) => updateFormData('industrySector', v)}>
                        <SelectTrigger className="border-slate-200" data-testid="industry-sector-select">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {INDUSTRY_SECTORS.map((sector) => (
                            <SelectItem key={sector.value} value={sector.value}>{sector.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="website">Corporate Website</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="website"
                        placeholder="https://www.yourcompany.com"
                        value={formData.website}
                        onChange={(e) => updateFormData('website', e.target.value)}
                        className="pl-10 border-slate-200"
                        data-testid="website-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Business Address</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="address"
                        placeholder="123 Federal Way"
                        value={formData.address}
                        onChange={(e) => updateFormData('address', e.target.value)}
                        className="pl-10 border-slate-200"
                        data-testid="address-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        placeholder="Reston"
                        value={formData.city}
                        onChange={(e) => updateFormData('city', e.target.value)}
                        className="border-slate-200"
                        data-testid="city-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        placeholder="VA"
                        value={formData.state}
                        onChange={(e) => updateFormData('state', e.target.value.toUpperCase())}
                        className="border-slate-200"
                        maxLength={2}
                        data-testid="state-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip">ZIP Code</Label>
                      <Input
                        id="zip"
                        placeholder="20191"
                        value={formData.zip}
                        onChange={(e) => updateFormData('zip', e.target.value)}
                        className="border-slate-200"
                        maxLength={10}
                        data-testid="zip-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: Primary Admin */}
              {step === 2 && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-900">Contractor Admin Role</p>
                        <p className="text-sm text-blue-700">
                          This person will have full administrative access to the workspace, including inviting team members,
                          linking with consultants, and managing all pursuits.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="adminFirstName">First Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="adminFirstName"
                          placeholder="Sarah"
                          value={formData.adminFirstName}
                          onChange={(e) => updateFormData('adminFirstName', e.target.value)}
                          className="pl-10 border-slate-200"
                          data-testid="admin-firstname-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="adminLastName">Last Name *</Label>
                      <Input
                        id="adminLastName"
                        placeholder="Johnson"
                        value={formData.adminLastName}
                        onChange={(e) => updateFormData('adminLastName', e.target.value)}
                        className="border-slate-200"
                        data-testid="admin-lastname-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="adminEmail">Work Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="adminEmail"
                        type="email"
                        placeholder="sarah@acmedefense.com"
                        value={formData.adminEmail}
                        onChange={(e) => updateFormData('adminEmail', e.target.value)}
                        className="pl-10 border-slate-200"
                        data-testid="admin-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="adminPhone">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="adminPhone"
                          placeholder="+1 (555) 123-4567"
                          value={formData.adminPhone}
                          onChange={(e) => updateFormData('adminPhone', e.target.value)}
                          className="pl-10 border-slate-200"
                          data-testid="admin-phone-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="adminTitle">Job Title</Label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="adminTitle"
                          placeholder="VP of Business Development"
                          value={formData.adminTitle}
                          onChange={(e) => updateFormData('adminTitle', e.target.value)}
                          className="pl-10 border-slate-200"
                          data-testid="admin-title-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3: Account Setup */}
              {step === 3 && (
                <div className="space-y-6">
                  <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-800">
                    You'll log in with a 6-digit code sent to your email — no password needed.
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="linkConsultant">Link with a Consultant (Optional)</Label>
                    <Input
                      id="linkConsultant"
                      placeholder="Enter consultant's email or company name"
                      value={formData.linkConsultant}
                      onChange={(e) => updateFormData('linkConsultant', e.target.value)}
                      className="border-slate-200"
                      data-testid="link-consultant-input"
                    />
                    <p className="text-xs text-slate-500">
                      If you're working with a consultant, enter their email to send a link request upon registration.
                    </p>
                  </div>

                  <div className="space-y-4 pt-4 border-t border-slate-200">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="agreeTerms"
                        checked={formData.agreeTerms}
                        onCheckedChange={(checked) => updateFormData('agreeTerms', checked)}
                        data-testid="agree-terms-checkbox"
                      />
                      <Label htmlFor="agreeTerms" className="text-sm text-slate-600 cursor-pointer">
                        I agree to the <a href="#" className="text-teal-700 hover:underline">Terms of Service</a> *
                      </Label>
                    </div>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="agreePrivacy"
                        checked={formData.agreePrivacy}
                        onCheckedChange={(checked) => updateFormData('agreePrivacy', checked)}
                        data-testid="agree-privacy-checkbox"
                      />
                      <Label htmlFor="agreePrivacy" className="text-sm text-slate-600 cursor-pointer">
                        I agree to the <a href="#" className="text-teal-700 hover:underline">Privacy Policy</a> *
                      </Label>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
                {step > 1 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(step - 1)}
                    className="border-slate-200"
                    data-testid="back-step-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                ) : (
                  <div />
                )}
                
                {step < 3 ? (
                  <Button
                    type="button"
                    onClick={() => setStep(step + 1)}
                    disabled={step === 1 ? !canProceedStep1 : !canProceedStep2}
                    className="bg-teal-700 hover:bg-teal-800 text-white"
                    data-testid="next-step-btn"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={!canSubmit || isLoading}
                    className="bg-teal-700 hover:bg-teal-800 text-white"
                    data-testid="submit-registration-btn"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        Create Account
                        <CheckCircle2 className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Help Text */}
        <p className="text-center text-slate-400 text-sm mt-6">
          Already have an account? <Link to="/login" className="text-teal-400 hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
